import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updateEmail,
  deleteUser
} from 'firebase/auth';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { db, auth } from '../firebase';
import { Visibility, VisibilityOff, Person as PersonIcon } from '@mui/icons-material';
import ResponsiveTitle from '../components/ResponsiveTitle';
import { useNavigate } from 'react-router-dom';

interface ProfileData {
  firstName: string;
  lastName: string;
  email: string;
  city: string;
  team: string;
}

interface PasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export const Profile = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState<ProfileData>({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    city: user?.city || '',
    team: user?.team || ''
  });

  const [passwordData, setPasswordData] = useState<PasswordData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswordSection, setShowPasswordSection] = useState(false);

  const handlePasswordChange = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('Les nouveaux mots de passe ne correspondent pas');
      setLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('Le nouveau mot de passe doit contenir au moins 6 caractères');
      setLoading(false);
      return;
    }

    try {
      const credential = EmailAuthProvider.credential(
        user?.email || '',
        passwordData.currentPassword
      );
      await reauthenticateWithCredential(auth.currentUser!, credential);
      await updatePassword(auth.currentUser!, passwordData.newPassword);

      setSuccess('Mot de passe mis à jour avec succès');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setOpenPasswordDialog(false);
    } catch (error: any) {
      if (error.code === 'auth/wrong-password') {
        setError('Le mot de passe actuel est incorrect');
      } else {
        setError('Une erreur est survenue lors de la mise à jour du mot de passe');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const credential = EmailAuthProvider.credential(
        user?.email || '',
        confirmPassword
      );
      await reauthenticateWithCredential(auth.currentUser!, credential);

      // Mettre à jour l'email si modifié
      if (profileData.email !== user?.email) {
        await updateEmail(auth.currentUser!, profileData.email);
      }

      // Mettre à jour les autres informations dans Firestore
      await updateDoc(doc(db, 'users', user?.uid || ''), {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        email: profileData.email,
        city: profileData.city,
        team: profileData.team
      });

      setSuccess('Profil mis à jour avec succès');
      setConfirmPassword('');
    } catch (error: any) {
      if (error.code === 'auth/wrong-password') {
        setError('Le mot de passe est incorrect');
      } else if (error.code === 'auth/email-already-in-use') {
        setError('Cette adresse email est déjà utilisée');
      } else {
        setError('Une erreur est survenue lors de la mise à jour du profil');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setError('');
    setLoading(true);

    try {
      const credential = EmailAuthProvider.credential(
        user?.email || '',
        confirmPassword
      );
      await reauthenticateWithCredential(auth.currentUser!, credential);

      // Supprimer les données Firestore
      await deleteDoc(doc(db, 'users', user?.uid || ''));

      // Supprimer le compte Firebase
      await deleteUser(auth.currentUser!);

      // Déconnexion et redirection
      await logout();
      navigate('/');
    } catch (error: any) {
      if (error.code === 'auth/wrong-password') {
        setError('Le mot de passe est incorrect');
      } else {
        setError('Une erreur est survenue lors de la suppression du compte');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleChangePassword = async () => {
    try {
      setError('');

      // Vérifications
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setError('Les nouveaux mots de passe ne correspondent pas');
        return;
      }

      if (passwordData.newPassword.length < 6) {
        setError('Le nouveau mot de passe doit contenir au moins 6 caractères');
        return;
      }

      const user = auth.currentUser;
      if (!user || !user.email) {
        setError('Erreur d\'authentification');
        return;
      }

      // Réauthentification avec le mot de passe actuel
      const credential = EmailAuthProvider.credential(
        user.email,
        passwordData.currentPassword
      );

      await reauthenticateWithCredential(user, credential);

      // Mise à jour du mot de passe
      await updatePassword(user, passwordData.newPassword);

      // Réinitialisation des champs
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setShowPasswordSection(false);

    } catch (error: any) {
      if (error.code === 'auth/wrong-password') {
        setError('Le mot de passe actuel est incorrect');
      } else {
        setError('Une erreur est survenue lors du changement de mot de passe');
      }
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        bgcolor: 'rgba(0, 0, 0, 0.5)',
        borderRadius: 4,
        px: 3,
        py: 1,
        color: 'white',
        width: 'fit-content',
        mb: 4
      }}>
        <PersonIcon />
        <ResponsiveTitle variant="h4">
          Mon Profil
        </ResponsiveTitle>
      </Box>

      <Paper sx={{ p: 3, bgcolor: '#333' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Prénom"
            value={profileData.firstName}
            onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
            fullWidth
          />
          <TextField
            label="Nom"
            value={profileData.lastName}
            onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
            fullWidth
          />
          <TextField
            label="Email"
            type="email"
            value={profileData.email}
            onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
            fullWidth
          />
          <TextField
            label="Ville"
            value={profileData.city}
            onChange={(e) => setProfileData({ ...profileData, city: e.target.value })}
            fullWidth
          />
          <TextField
            label="Team"
            value={profileData.team}
            onChange={(e) => setProfileData({ ...profileData, team: e.target.value })}
            fullWidth
          />
          <TextField
            label="Confirmer le mot de passe"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            fullWidth
            required
            helperText="Entrez votre mot de passe pour confirmer les modifications"
          />
          <Button
            variant="contained"
            onClick={handleProfileUpdate}
            disabled={loading || !confirmPassword}
          >
            Mettre à jour le profil
          </Button>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setShowPasswordSection(true)}
              disabled={loading}
            >
              Changer le mot de passe
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setOpenDeleteDialog(true)}
              disabled={loading}
            >
              Supprimer le compte
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleLogout}
              disabled={loading}
            >
              Se déconnecter
            </Button>
          </Box>

          {showPasswordSection && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Changement de mot de passe
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Mot de passe actuel"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          edge="end"
                        >
                          {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label="Nouveau mot de passe"
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label="Confirmer le nouveau mot de passe"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {error && (
                  <Typography color="error" variant="body2">
                    {error}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    onClick={() => {
                      setShowPasswordSection(false);
                      setPasswordData({
                        currentPassword: '',
                        newPassword: '',
                        confirmPassword: ''
                      });
                      setError('');
                    }}
                    color="inherit"
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={handleChangePassword}
                    color="primary"
                    variant="contained"
                    disabled={!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                  >
                    Changer le mot de passe
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Dialog pour la suppression du compte */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { bgcolor: '#333', color: '#fff' }
        }}
      >
        <DialogTitle>Supprimer le compte</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Êtes-vous sûr de vouloir supprimer votre compte ? Cette action est irréversible.
          </Typography>
          <TextField
            label="Confirmer le mot de passe"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            fullWidth
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} sx={{ color: '#fff' }}>
            Annuler
          </Button>
          <Button
            onClick={handleDeleteAccount}
            variant="contained"
            color="error"
            disabled={loading || !confirmPassword}
          >
            Supprimer le compte
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
